// src/oura/components/visibility/FixWording.tsx
//
// Fix one question's wording on the latest check and ask only it again, from
// the question's drawer on Visibility (Jack, 22 Oct 2026). Until now the same
// route (POST /assessments/{id}/questions/{n}/correct, backend 25ad7f07) was
// reachable only from Edit and rescan on the first check's review, so a typo
// found later (Brickhouse "Beong") needed Ipul's keys.
//
// Same rules as Re-ask (newscan/Reask.tsx): a free dry run first, so a refused
// wording costs nothing; one paid ask for this question only; the other
// questions keep their answers. The page is re-read once the answers land; a
// re-read renames the question, so the drawer says it was done (FixedNotice).

import { useEffect, useRef, useState } from "react";
import { CheckIcon, Loader2Icon, RefreshCwIcon } from "lucide-react";
import { correctQuestion, getQuestionCorrections } from "@service/aeo";
import { PillButton, SURFACE, TYPE } from "@oura/ui";
import { useWritesPaused } from "@context/maintenance";
import { cn } from "../../lib/cn";

// Ipul's handoff (20 Oct): poll about every 10 seconds; a re-ask takes a few minutes.
const POLL_MS = 10_000;
const WAIT_MS = 6 * 60 * 1000;

const COPY = {
    title: "Fix this question",
    intro: "Change the words, then Oura asks only this question again on every AI. The other questions keep their answers.",
    label: "New wording",
    was: "Was:",
    cost: "This runs a paid check for this question only. The score updates once the new answers are in.",
    cancel: "Cancel",
    press: "Fix and re-ask",
    checking: "Checking the new wording",
    asking: "Asking the new wording",
    askingNote: "Takes a few minutes. You can close this and Oura finishes in the background.",
    failed: "It was reworded, but the new answers did not come back. Try it again from here.",
    unknown: "Sent, but the result could not be read. Refresh the page before sending it again.",
    busy: "Another question on this check is being asked. Try again in a minute.",
    same: "Type the new words first.",
    checkFailed: "The new wording could not be checked. Try again.",
} as const;

type Phase = "editing" | "checking" | "asking" | "failed" | "unknown";

export function FixWording({
    assessmentId,
    n,
    text,
    onCancel,
    onDone,
}: {
    assessmentId: string;
    /** The question's slot on the check (VisRow.id). */
    n: number;
    text: string;
    onCancel: () => void;
    /** The new answers are in: the drawer says so and re-reads the check. */
    onDone: (was: string) => void | Promise<void>;
}) {
    const [value, setValue] = useState(text);
    const [phase, setPhase] = useState<Phase>("editing");
    const [error, setError] = useState<string | null>(null);
    const [was, setWas] = useState(text);
    const writesPaused = useWritesPaused();
    // Closing the drawer or opening another question ends the wait, never a sent ask.
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);
    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => { inputRef.current?.focus(); }, []);

    const next = value.trim();
    const changed = next.length > 0 && next !== text.trim();

    const waitFor = async (): Promise<"asked" | "failed" | "unknown" | "gone"> => {
        const until = Date.now() + WAIT_MS;
        while (Date.now() < until) {
            await new Promise((r) => setTimeout(r, POLL_MS));
            if (!alive.current) return "gone";
            try {
                const state = (await getQuestionCorrections(assessmentId))[String(n)]?.state;
                if (state === "asked") return "asked";
                if (state === "failed" || state === "clear_failed") return "failed";
            } catch {
                // A failed read is not a failed re-ask: keep waiting.
            }
        }
        return "unknown";
    };

    const submit = async () => {
        if (!changed) { setError(COPY.same); return; }
        setError(null);
        setPhase("checking");
        let dry: Awaited<ReturnType<typeof correctQuestion>>;
        try {
            dry = await correctQuestion(assessmentId, n, next, { dryRun: true });
        } catch {
            // The free check spends nothing, so trying again is safe.
            if (!alive.current) return;
            setError(COPY.checkFailed);
            setPhase("editing");
            return;
        }
        if (!alive.current) return;
        if (!dry.ok) {
            setError(dry.code === "ask_in_progress" ? COPY.busy : dry.message);
            setPhase("editing");
            return;
        }
        let res: Awaited<ReturnType<typeof correctQuestion>>;
        try {
            res = await correctQuestion(assessmentId, n, next);
        } catch {
            // The paid ask may have reached the server: never offer an immediate resend.
            if (!alive.current) return;
            setWas(text);
            setPhase("unknown");
            return;
        }
        if (!alive.current) return;
        if (!res.ok) {
            setError(res.code === "ask_in_progress" ? COPY.busy : res.message);
            setPhase("editing");
            return;
        }
        const before = res.was || text;
        setWas(before);
        setPhase("asking");
        const state = await waitFor();
        if (state === "gone") return;
        if (state !== "asked") {
            setPhase(state);
            return;
        }
        // The notice shows only once the new answers are read in; a failed read says refresh.
        try {
            await onDone(before);
        } catch {
            if (alive.current) setPhase("unknown");
        }
    };

    if (phase === "asking" || phase === "checking") {
        return (
            <section className={cn(SURFACE.block, "flex items-start gap-3 p-5")} aria-live="polite">
                <Loader2Icon aria-hidden size={18} className="mt-0.5 shrink-0 animate-spin text-blue-600" />
                <div className="min-w-0">
                    <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{phase === "checking" ? COPY.checking : COPY.asking}</p>
                    {phase === "asking" && <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{COPY.askingNote}</p>}
                </div>
            </section>
        );
    }
    if (phase === "unknown" || phase === "failed") {
        return (
            <section className={cn(SURFACE.block, "flex items-start gap-3 p-5")} aria-live="polite">
                <span aria-hidden className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-amber-500 text-xs font-semibold text-white">!</span>
                <div className="min-w-0 flex-1">
                    <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{phase === "failed" ? COPY.failed : COPY.unknown}</p>
                    <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{COPY.was} "{was}"</p>
                    {phase === "failed" && (
                        <div className="mt-3">
                            <PillButton size="sm" icon={RefreshCwIcon} writes onClick={() => void submit()}>{COPY.press}</PillButton>
                        </div>
                    )}
                </div>
            </section>
        );
    }

    return (
        <section className={cn(SURFACE.block, "p-5")}>
            <p className={cn(TYPE.tt, "m-0 text-gray-900")}>{COPY.title}</p>
            <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>{COPY.intro}</p>
            <label htmlFor={`fix-wording-${n}`} className={cn(TYPE.lab, "mt-4 block")}>{COPY.label}</label>
            <input
                id={`fix-wording-${n}`}
                ref={inputRef}
                value={value}
                onChange={(e) => { setValue(e.target.value); setError(null); }}
                onKeyDown={(e) => { if (e.key === "Enter" && !writesPaused) void submit(); }}
                className={cn(TYPE.tb, "mt-1 block h-10 w-full rounded-xl border-0 bg-gray-50 px-3 text-gray-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600")}
            />
            <p className={cn(TYPE.tc, "m-0 mt-1.5 text-gray-500")}>{COPY.was} {text}</p>
            {error && <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 text-rose-600")}>{error}</p>}
            <p className={cn(TYPE.tc, "m-0 mt-4 rounded-xl bg-gray-50 p-3 text-gray-600")}>{COPY.cost}</p>
            <div className="mt-4 flex justify-end gap-2">
                <PillButton onClick={onCancel}>{COPY.cancel}</PillButton>
                <PillButton variant="primary" icon={RefreshCwIcon} writes disabled={!changed} onClick={() => void submit()}>{COPY.press}</PillButton>
            </div>
        </section>
    );
}

/** After a fix landed and the check was re-read: said once, on the renamed question. */
export function FixedNotice({ was }: { was: string }) {
    return (
        <section className={cn(SURFACE.block, "flex items-start gap-3 p-4")} aria-live="polite">
            <span aria-hidden className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-emerald-500 text-white">
                <CheckIcon size={13} strokeWidth={3} />
            </span>
            <div className="min-w-0">
                <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>Reworded and asked again</p>
                <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>These are the new answers. Was: "{was}"</p>
            </div>
        </section>
    );
}
